import React, { useState } from 'react';
import { FileText, Shield, AlertCircle, CheckCircle, Clock, MapPin, Wrench, DollarSign, Scale, Phone, Mail } from 'lucide-react';

const TermsAndConditions = () => {
  const [activeSection, setActiveSection] = useState(null);

  const terms = [
    {
      id: 'responsibility',
      icon: <Shield className="w-6 h-6" />,
      title: 'Customer Responsibility',
      color: 'bg-blue-100 text-blue-600',
      content: 'The customer is responsible for the vehicle from the time it is picked up from the store location until it is returned to the store location. You are liable for all aspects of the vehicle during the rental period.'
    },
    {
      id: 'tyre-check',
      icon: <AlertCircle className="w-6 h-6" />,
      title: 'Tyre Pressure Check',
      color: 'bg-orange-100 text-orange-600',
      content: 'Please check the air pressure of tyres at the time of pick up the vehicle. Any complaint related to tyre pressure will not be entertained later. It is your responsibility to verify the vehicle condition at pickup.'
    },
    {
      id: 'external-repairs',
      icon: <Wrench className="w-6 h-6" />,
      title: 'External Repair Reimbursement',
      color: 'bg-purple-100 text-purple-600',
      content: 'If the customer pays charges for any damage caused to the vehicle outside of the company garage, the company has no responsibility to return the same to the customer. You must obtain confirmation from any authorized person of the company and bring an actual bill copy if you wish to be reimbursed.'
    },
    {
      id: 'vehicle-condition',
      icon: <CheckCircle className="w-6 h-6" />,
      title: 'Vehicle Return Condition',
      color: 'bg-green-100 text-green-600',
      content: 'The bike should be in the same condition as when it was picked up from company location. Any damages, missing parts, or excessive wear will be charged to the customer.'
    },
    {
      id: 'gps-tracking',
      icon: <MapPin className="w-6 h-6" />,
      title: 'GPS Tracking',
      color: 'bg-red-100 text-red-600',
      content: 'The company will be able to see the location of the vehicle instantly because it has been installed with a GPS tracker. This ensures vehicle security and helps in case of theft or emergencies.'
    },
    {
      id: 'booking-cancellation',
      icon: <Clock className="w-6 h-6" />,
      title: 'Booking Cancellation',
      color: 'bg-yellow-100 text-yellow-600',
      content: 'The booking amount will not be refunded if the customer cancels their booking or fails to come up to pick up the bike within the allotted period. Cancellations must be made at least 2 hours before the scheduled pickup time.'
    },
    {
      id: 'late-return',
      icon: <Clock className="w-6 h-6" />,
      title: 'Late Return Charges',
      color: 'bg-indigo-100 text-indigo-600',
      content: 'The business could apply additional costs if the bike is not returned within the allotted period. Late return fees will be charged as per company policy.'
    },
    {
      id: 'booking-extension',
      icon: <Clock className="w-6 h-6" />,
      title: 'Booking Extension',
      color: 'bg-teal-100 text-teal-600',
      content: 'For weekly, fortnightly and monthly bookings, customers must notify the company one day before the booking expires. For daily bookings, notification must be given 3-4 hours before the booking expires.'
    },
    {
      id: 'non-operational',
      icon: <AlertCircle className="w-6 h-6" />,
      title: 'Non-Operational Vehicle',
      color: 'bg-red-100 text-red-600',
      content: 'If the vehicle is in not operating status at the time of drop at site/Company Location, the damage compensation will be deducted from the deposit amount and may charge additional as required. If customer does not agree, the company can take legal action.'
    },
    {
      id: 'early-return',
      icon: <DollarSign className="w-6 h-6" />,
      title: 'Early Return Policy',
      color: 'bg-yellow-100 text-yellow-600',
      content: 'Rent will not be returned for remaining days in case customer returns the vehicle before due date of the booking. Only the deposit will be refunded after inspection.'
    },
    {
      id: 'damage-liability',
      icon: <Shield className="w-6 h-6" />,
      title: 'Damage Liability',
      color: 'bg-orange-100 text-orange-600',
      content: 'Customer is responsible to pay for the damage of the vehicle caused by accident, mishandling, carelessness, etc. during the possession of the vehicle. All repair costs will be assessed and charged accordingly.'
    },
    {
      id: 'traffic-challan',
      icon: <Scale className="w-6 h-6" />,
      title: 'Traffic Violations',
      color: 'bg-purple-100 text-purple-600',
      content: 'Any challan caused by traffic rules violation during possession of vehicle is liable to the customer to pay at company office. The customer is responsible for all fines and penalties incurred during the rental period.'
    },
    {
      id: 'helmet-damage',
      icon: <AlertCircle className="w-6 h-6" />,
      title: 'Helmet Damage Charges',
      color: 'bg-blue-100 text-blue-600',
      content: 'In case the helmet is damaged, the customer is required to pay a charge which is decided by the company. Helmets are provided as a courtesy and must be returned in good condition.'
    },
    {
      id: 'fuel-policy',
      icon: <DollarSign className="w-6 h-6" />,
      title: 'Fuel Policy',
      color: 'bg-green-100 text-green-600',
      content: 'At the time when the customer rents the bike, there is enough fuel to go down to the nearby fuel station. It is necessary to preserve enough fuel in the vehicle to reach the fuel station again when the customer returns the vehicle to the company.'
    },
    {
      id: 'deposit-refund',
      icon: <DollarSign className="w-6 h-6" />,
      title: 'Deposit Refund Timeline',
      color: 'bg-indigo-100 text-indigo-600',
      content: 'The deposit amount will be given to the customer within 24 hours after checking the vehicle\'s working condition, any damages or traffic challan. Refunds are processed to the original payment method.'
    }
  ];

  const categories = [
    {
      name: 'Vehicle & Safety',
      icon: '🏍️',
      terms: ['responsibility', 'tyre-check', 'vehicle-condition', 'gps-tracking']
    },
    {
      name: 'Financial Terms',
      icon: '💰',
      terms: ['booking-cancellation', 'late-return', 'early-return', 'deposit-refund']
    },
    {
      name: 'Damage & Liability',
      icon: '🔧',
      terms: ['external-repairs', 'non-operational', 'damage-liability', 'helmet-damage']
    },
    {
      name: 'Operational Policies',
      icon: '📋',
      terms: ['booking-extension', 'traffic-challan', 'fuel-policy']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 to-red-800 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center mb-6">
            <FileText className="w-12 h-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">
              Terms & Conditions
            </h1>
          </div>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Please read these terms carefully before renting a vehicle from Vegatas Bike Rental Service
          </p>
        </div>
      </div>

      {/* Important Notice */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-6 shadow-md">
          <div className="flex items-start space-x-4">
            <AlertCircle className="w-8 h-8 text-yellow-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Agreement Acknowledgment
              </h3>
              <p className="text-gray-700 leading-relaxed">
                By renting a vehicle from Vegatas Bike Rental Service, you acknowledge that you have read, understood, and agree to comply with all terms and conditions outlined below. Violation of these terms may result in legal action and forfeiture of your security deposit.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
          Browse by Category
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {categories.map((category, index) => (
            <a
              key={index}
              href={`#${category.terms[0]}`}
              className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
            >
              <div className="text-4xl mb-3">{category.icon}</div>
              <h3 className="font-semibold text-gray-900">
                {category.name}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {category.terms.length} terms
              </p>
            </a>
          ))}
        </div>
      </div>

      {/* Terms Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
          Detailed Terms & Conditions
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          Review all terms carefully to ensure a smooth rental experience
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {terms.map((term, index) => (
            <div
              key={term.id}
              id={term.id}
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300 border border-gray-100"
            >
              <div className={`${term.color} w-14 h-14 rounded-full flex items-center justify-center mb-4`}>
                {term.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {index + 1}. {term.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {term.content}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Key Highlights */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Key Highlights
          </h2>
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
            <div className="space-y-6">
              <div className="flex items-start space-x-4 pb-6 border-b border-gray-200">
                <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Full Responsibility</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    You are fully responsible for the vehicle from pickup to return. Inspect thoroughly at pickup and report any existing damage immediately.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 pb-6 border-b border-gray-200">
                <div className="bg-green-100 p-2 rounded-lg flex-shrink-0">
                  <Clock className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Extension Requests</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    For bookings over 3 days, notify us 24 hours before expiry. For daily bookings, notify 3-4 hours in advance to avoid late fees.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 pb-6 border-b border-gray-200">
                <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0">
                  <DollarSign className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">No Partial Refunds</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Early returns do not qualify for rental refunds. Only the security deposit is refunded after vehicle inspection.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 pb-6 border-b border-gray-200">
                <div className="bg-orange-100 p-2 rounded-lg flex-shrink-0">
                  <Scale className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Traffic Violations</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    All traffic challans and fines incurred during your rental period are your responsibility and must be paid at our office.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-red-100 p-2 rounded-lg flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">GPS Monitoring</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    All vehicles are equipped with GPS trackers for security purposes. The company reserves the right to track vehicle location at any time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Obligations */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
          Your Obligations as a Customer
        </h2>
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-blue-500">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <CheckCircle className="w-6 h-6 text-blue-500 mr-3" />
              You Must
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <span className="text-blue-500 font-bold">•</span>
                <span className="text-gray-700 text-sm">Inspect the vehicle thoroughly at pickup time</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-blue-500 font-bold">•</span>
                <span className="text-gray-700 text-sm">Return the vehicle in the same condition as received</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-blue-500 font-bold">•</span>
                <span className="text-gray-700 text-sm">Maintain sufficient fuel to reach the nearest fuel station</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-blue-500 font-bold">•</span>
                <span className="text-gray-700 text-sm">Notify company for booking extensions in advance</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-blue-500 font-bold">•</span>
                <span className="text-gray-700 text-sm">Pay all traffic fines and violations during rental period</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-blue-500 font-bold">•</span>
                <span className="text-gray-700 text-sm">Get company authorization before external repairs</span>
              </li>
            </ul>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-red-500">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <AlertCircle className="w-6 h-6 text-red-500 mr-3" />
              You Must Not
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <span className="text-red-500 font-bold">•</span>
                <span className="text-gray-700 text-sm">Return the vehicle in non-operational condition</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-red-500 font-bold">•</span>
                <span className="text-gray-700 text-sm">Expect refunds for early returns or cancellations after pickup</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-red-500 font-bold">•</span>
                <span className="text-gray-700 text-sm">Damage or misuse the vehicle and accessories</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-red-500 font-bold">•</span>
                <span className="text-gray-700 text-sm">Get repairs done without company authorization</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-red-500 font-bold">•</span>
                <span className="text-gray-700 text-sm">Violate traffic rules or regulations during rental</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-red-500 font-bold">•</span>
                <span className="text-gray-700 text-sm">Return the vehicle late without prior notification</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Legal Notice */}
      <div className="bg-gray-900 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <Scale className="w-12 h-12 mx-auto mb-4 text-blue-400" />
            <h2 className="text-3xl font-bold mb-4">Legal Enforcement</h2>
            <p className="text-gray-300">
              These terms and conditions constitute a legally binding agreement
            </p>
          </div>
          <div className="bg-gray-800 rounded-xl p-8 text-sm text-gray-300 leading-relaxed">
            <p className="mb-4">
              By renting a vehicle from Vegatas Bike Rental Service, you enter into a legally binding contract. Failure to comply with these terms may result in:
            </p>
            <ul className="space-y-2 mb-4">
              <li className="flex items-start space-x-2">
                <span>•</span>
                <span>Forfeiture of security deposit</span>
              </li>
              <li className="flex items-start space-x-2">
                <span>•</span>
                <span>Additional charges for damages or violations</span>
              </li>
              <li className="flex items-start space-x-2">
                <span>•</span>
                <span>Legal action to recover costs and damages</span>
              </li>
              <li className="flex items-start space-x-2">
                <span>•</span>
                <span>Blacklisting from future rentals</span>
              </li>
            </ul>
            <p className="text-xs text-gray-400 border-t border-gray-700 pt-4 mt-4">
              Vegatas Bike Rental Service reserves the right to modify these terms and conditions at any time without prior notice. The latest version will always be available on our website. Continued use of our services after modifications constitutes acceptance of updated terms.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-2xl shadow-2xl p-8 md:p-12 text-white">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">Questions About Our Terms?</h2>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              If you need clarification on any terms or have concerns, please contact us before completing your booking
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <a
              href="tel:9730736858"
              className="bg-white text-blue-600 rounded-lg p-6 hover:bg-blue-50 transition-colors duration-200 flex items-center space-x-4 shadow-lg"
            >
              <div className="bg-blue-100 p-3 rounded-full">
                <Phone className="w-6 h-6" />
              </div>
              <div className="text-left">
                <div className="text-sm text-gray-600 font-medium">Call Us</div>
                <div className="text-lg font-semibold">9730736858</div>
              </div>
            </a>
            <a
              href="mailto:Vegatasbike00@gmail.com"
              className="bg-white text-blue-600 rounded-lg p-6 hover:bg-blue-50 transition-colors duration-200 flex items-center space-x-4 shadow-lg"
            >
              <div className="bg-blue-100 p-3 rounded-full">
                <Mail className="w-6 h-6" />
              </div>
              <div className="text-left">
                <div className="text-sm text-gray-600 font-medium">Email Us</div>
                <div className="text-lg font-semibold break-all">Vegatasbike00@gmail.com</div>
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="max-w-7xl mx-auto px-4 py-8 border-t border-gray-200">
        <div className="text-center text-sm text-gray-500">
          <p className="mb-2">Last Updated: October 2025</p>
          <p>
            © 2025 Vegatas Bike Rental Service. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
